import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Dayjs } from 'dayjs';
import { tKey } from 'ember-intl';
import type { ComparisonSet } from 'frontend-utils/utils/constants/benchmark-insight';
import type { BeniBreakdown, BeniMetaComparisonSet } from 'lumen/models';
import {
  BREAKDOWN_PERIOD_TYPES,
  type BreakdownPeriodType,
} from 'lumen/models/benchmark-insight/breakdown';
import type {
  BeniBreakdownType,
  Kpi,
  ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { DropdownComparisonSetsOptionNone } from 'lumen/utils/constants/benchmark-insight/comparison-set-selection/dropdown-comparison-sets';
import type { DateAggregationType } from 'lumen/utils/constants/general-constants';

export interface DateRangeType {
  end: Dayjs;
  start: Dayjs;
}

export interface Column extends CustomEmberTableColumn {
  columnDataType?: string;
  comparisonSetType?: ComparisonSet;
  hasRightBorder?: boolean;
  hidden?: boolean;
  period?: BreakdownPeriodType;
  showUpsellIcon?: boolean;
  subcolumns?: Column[];
}

export interface ColumnConfig {
  breakdown: BeniBreakdownType;
  dateAggregation: DateAggregationType;
  ownComparisonSetMeta: BeniMetaComparisonSet | null;
  primaryComparisonSetMeta: BeniMetaComparisonSet | null;
  primaryComparisonSetType: ComparisonSet | null;
  referencePeriod: ReferencePeriod;
  secondaryComparisonSetMeta: BeniMetaComparisonSet | null;
  secondaryComparisonSetType: ComparisonSet | DropdownComparisonSetsOptionNone;
  selectedRange: DateRangeType;
  visibleKpis: Kpi[];
}

export interface SegmentItem extends CustomEmberTableRow {
  categoryId?: string;
  children?: SegmentItem[];
  date?: string | number | Date | undefined | Dayjs;
  depth?: number;
  id?: string | null;
  isParent?: boolean;
  isTotalRow?: boolean;
  label?: string;
  order?: number;
  value?: string;
}

export interface CombinedSegmentation extends SegmentItem {
  className?: string;
  ownComparisonSetBreakdown: BeniBreakdown | null;
  primaryComparisonSetBreakdown: BeniBreakdown | null;
  secondaryComparisonSetBreakdown: BeniBreakdown | null;
}

export const BENI_TABLE_ROW_TYPES = {
  TOTAL: 'total',
} as const;

export const BENI_TABLE_COLUMN_TYPES = {
  DATE: 'date',
  LABEL: 'label',
  RANKING: 'ranking',
  DIFF_COMPARISON: 'diff_comparison',
} as const;
type TableColumnType =
  (typeof BENI_TABLE_COLUMN_TYPES)[keyof typeof BENI_TABLE_COLUMN_TYPES];

export const BENI_TABLE_COLUMN_FORMATS = {
  DIFF: 'diff',
  INDEX: 'index',
  RANKING: 'ranking',
} as const;

export const BENI_TABLE_LABELS: Record<TableColumnType, string> = {
  [BENI_TABLE_COLUMN_TYPES.DATE]: tKey('benchmark_insight.stay_date'),
  [BENI_TABLE_COLUMN_TYPES.LABEL]: tKey('benchmark_insight.segmentation'),
  [BENI_TABLE_COLUMN_TYPES.RANKING]: tKey(
    'benchmark_insight.table.headers.ranking_market',
  ),
  [BENI_TABLE_COLUMN_TYPES.DIFF_COMPARISON]: tKey(
    'benchmark_insight.difference_vs_comparison_set',
  ),
} as const;

export const PERIOD_VALUE_PATH_BY_BREAKDOWN_PERIOD_TYPE = {
  [BREAKDOWN_PERIOD_TYPES.CURRENT]: 'current',
  [BREAKDOWN_PERIOD_TYPES.REFERENCE]: 'ref',
} as const;
